import { StoryConfig } from 'services/story.service';
import { roundTime, selectClipData } from 'components/app/_helpers/_app.helper';
import { Clip, ClipsMap, LinkedClips } from 'components/app/editor/_mediator/editor.slice';
import { getLinkData } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { SbPlaybackParams } from 'components/app/storyboard/_mediator/sb.slice';

type GetPlaybackPayloads = {
  configRate: number;
  clipData: Clip;
  cellDuration: number;
  clipsMap: ClipsMap;
  linkedClips: LinkedClips;
  index: Index;
};

type LinkedData = ReturnType<typeof getLinkData>;

const getStorySpeed = (storyConfig: StoryConfig | null) => {
  if (!storyConfig) return;
  return storyConfig.playbackRate;
};

export const getConfigRate = (clipData: Clip, storyConfig: StoryConfig | null) => {
  const clipSpeed = clipData.config.speed;
  const storySpeed = getStorySpeed(storyConfig);
  if (!storySpeed) return 1;
  if (typeof clipSpeed === 'number') return storySpeed * clipSpeed;
  return storySpeed;
};

export const getPlaybackParams = ({
  cellDuration,
  configRate,
  clipData,
  clipsMap,
  index,
  linkedClips,
}: GetPlaybackPayloads): SbPlaybackParams => {
  const handleTime = (clip: Clip) => {
    const duration = cellDuration * configRate;
    return clip.startTime !== null ? clip.startTime + duration : clip.endTime! - duration;
  };

  const getStartTime = (clip: Clip, linkedData: LinkedData): Seconds => {
    if (clip.startTime !== null) return clip.startTime!;
    if (!linkedData) return handleTime(clip);
    const { group, index, isFirstInGroup } = linkedData;
    if (!isFirstInGroup) return selectClipData(group[index - 1], clipsMap).endTime!;
    return handleTime(clip);
  };

  const getEndTime = (clip: Clip, linkedData: LinkedData): Seconds => {
    if (clip.endTime !== null) return clip.endTime!;
    if (!linkedData) return handleTime(clip);
    const { group, index, isLastInGroup } = linkedData;
    if (!isLastInGroup) return selectClipData(group[index + 1], clipsMap).startTime!;
    return handleTime(clip);
  };

  const linkedData = getLinkData(clipData, linkedClips);
  const startTime = getStartTime(clipData, linkedData);
  const endTime = getEndTime(clipData, linkedData);
  const rate = roundTime((endTime - startTime) / cellDuration);

  const linkedIndex = linkedData && { ...linkedData, group: undefined };

  return {
    startTime,
    endTime,
    rate,
    cellIndex: index,
    idleStart: startTime < 0 ? Math.abs(startTime) : 0,
    idleEnd: endTime < 0 ? Math.abs(endTime) : 0,
    linkedIndex,
  };
};
